import type {
  PatientDossierResponseData,
  PatientDossierSectionResult,
} from '../../../../../helpers/api/patients/patients';

type TranslateFn = (key: string) => string;

const DOSSIER_SECTION_ORDER = [
  'patient',
  'pregnancy',
  'family_history',
  'patient_medical_history',
  'prior_deliveries',
] as const;

const DOSSIER_SECTION_LABEL_KEYS: Record<string, string> = {
  patient: 'dossier_section_patient',
  pregnancy: 'dossier_section_pregnancy',
  family_history: 'dossier_section_family_history',
  patient_medical_history: 'dossier_section_patient_medical_history',
  prior_deliveries: 'dossier_section_prior_deliveries',
};

export interface DossierSectionDisplay {
  section: string;
  label: string;
  message?: string;
}

export interface DossierSectionSummary {
  saved: DossierSectionDisplay[];
  failed: DossierSectionDisplay[];
}

const sectionSortValue = (section: string) => {
  const index = DOSSIER_SECTION_ORDER.indexOf(
    section as (typeof DOSSIER_SECTION_ORDER)[number]
  );
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const bySectionOrder = <T extends { section: string }>(items: T[]) =>
  [...items].sort(
    (left, right) =>
      sectionSortValue(left.section) - sectionSortValue(right.section)
  );

export const getDossierSectionLabel = (t: TranslateFn, section: string) => {
  const translationKey = DOSSIER_SECTION_LABEL_KEYS[section];
  return translationKey ? t(translationKey) : section.replace(/_/g, ' ');
};

const toDisplaySection = (
  t: TranslateFn,
  result: PatientDossierSectionResult
): DossierSectionDisplay => ({
  section: result.section,
  label: getDossierSectionLabel(t, result.section),
  ...(result.message ? { message: result.message } : {}),
});

const hasOwnKey = (value: Record<string, unknown>, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);

const buildSummaryFromResults = (
  t: TranslateFn,
  sectionResults: PatientDossierSectionResult[]
): DossierSectionSummary => {
  const saved: DossierSectionDisplay[] = [];
  const failed: DossierSectionDisplay[] = [];

  bySectionOrder(sectionResults).forEach((result) => {
    if (result.status === 'failed') {
      failed.push(toDisplaySection(t, result));
      return;
    }
    if (result.status === 'saved') {
      saved.push(toDisplaySection(t, result));
    }
  });

  return { saved, failed };
};

export const buildDossierSectionSummary = ({
  submittedData,
  responseData,
  t,
}: {
  submittedData?: object | null;
  responseData?: PatientDossierResponseData | null;
  t: TranslateFn;
}): DossierSectionSummary => {
  const sectionResults = Array.isArray(responseData?.section_results)
    ? responseData.section_results
    : [];
  if (sectionResults.length > 0) {
    return buildSummaryFromResults(t, sectionResults);
  }

  const payload = (submittedData ?? {}) as Record<string, unknown>;
  const failedMessages = new Map(
    (responseData?.errors ?? []).map((error) => [error.section, error.message])
  );

  const saved: DossierSectionDisplay[] = [];
  const failed: DossierSectionDisplay[] = [];

  DOSSIER_SECTION_ORDER.forEach((section) => {
    if (!hasOwnKey(payload, section)) {
      return;
    }

    const message = failedMessages.get(section);
    const displaySection = {
      section,
      label: getDossierSectionLabel(t, section),
    };

    if (message) {
      failed.push({ ...displaySection, message });
      return;
    }

    saved.push(displaySection);
  });

  return { saved, failed };
};
